/**
 * The Hygge palette as a spreadsheet writer needs it, in one place.
 *
 * Two exports build styled workbooks — the absence schedule and the Time
 * Tracker's report builder — and draw on one palette plus one hex→ARGB helper.
 * One copy each would mean finding both on the next brand change, and a
 * document downloaded from Absences and one downloaded from the Time Tracker
 * should look like they came from the same company.
 *
 * Six of the seven values have two consumers, so read the notes below before
 * retuning one: `beige` is both a report's group-total tint and the schedule's
 * weekend-cell tint, and a nudge that makes totals pop will restyle somebody's
 * calendar.
 *
 * `teal`, `cream` and `border` approximate `--hygge`, `--card` and `--border`
 * in `src/styles/globals.css`; `tealSoft` and `beige` are tints derived from
 * them, and the two text colours were chosen to sit on the fills. Kept as
 * literals by necessity — a workbook cannot read a stylesheet — so when a brand
 * colour changes there, change it here too.
 */

/**
 * Hex → the 8-character ARGB ExcelJS wants, alpha first.
 *
 * Every cell either export paints is opaque, so the alpha is always `FF`.
 * Accepts `#RRGGBB` or `RRGGBB`.
 */
export const argb = (hex: string): string => `FF${hex.replace('#', '').toUpperCase()}`;

export const XLSX_BRAND = {
  /** `--hygge` / `--primary` — title and header bands, in both exports. */
  teal: '007553',
  /**
   * A softer, greyer green that still carries cream text at 4.7:1. The
   * schedule tints its WEEKEND day-headers with it, so the eye does not wander
   * into them; the report export does not use it yet.
   */
  tealSoft: '417969',
  /**
   * The site's Surface 2 — text on teal, and the fill of a label column or a
   * footer. One step off white rather than `--card` itself, because a day
   * cell is white and the label zone has to stand apart from it.
   */
  cream: 'F8F7F5',
  /**
   * A warmer tint than cream. Two jobs: the schedule's empty WEEKEND cells,
   * and a report row that has to read as its group's total.
   */
  beige: 'ECE9E4',
  /** `--border` — the hairline that gives the eye a track along a wide row. */
  border: 'E0E0E0',
  /** Secondary text: the schedule's department labels, a report's body rows. */
  mutedText: '5E5E58',
  /** Primary text on a light fill: names, and a total's figures. */
  foreground: '1B1B19',
} as const;
